import React, { useEffect, useState } from 'react';
import { XIcon, AlertTriangleIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useAuth } from './auth/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { deleteUser, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth } from '../firebase/firebaseConfig';
import { deleteUserAccountData } from '../firebase/auth';
interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose
}) => {
  const {
    logout,
    user
  } = useAuth();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setConfirmText('');
      setPassword('');
      setShowPassword(false);
      setError('');
      setIsDeleting(false);
    }
  }, [isOpen]);
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save the current overflow style
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const originalPaddingRight = window.getComputedStyle(document.body).paddingRight;
      // Calculate scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      // Prevent scrolling on the body
      document.body.style.overflow = 'hidden';
      // Add padding right to prevent layout shift when scrollbar disappears
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      // Restore the original style when modal closes
      return () => {
        document.body.style.overflow = originalStyle;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const handleDeleteAccount = async () => {
    // Validate confirmation text
    if (confirmText !== 'DELETE') {
      setError('Please type DELETE to confirm account deletion');
      return;
    }
    // Validate password is provided
    if (!password.trim()) {
      setError('Please enter your password to verify your identity');
      return;
    }
    setIsDeleting(true);
    setError('');
    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !user?.id) {
        throw new Error('User not found. Please sign in again.');
      }
      if (!currentUser.email) {
        throw new Error('No email associated with this account.');
      }
      // Step 1: Re-authenticate the user with their password
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      try {
        await reauthenticateWithCredential(currentUser, credential);
      } catch (reauthError: any) {
        if (reauthError.code === 'auth/wrong-password' || reauthError.code === 'auth/invalid-credential') {
          throw new Error('Incorrect password. Please try again.');
        }
        if (reauthError.code === 'auth/too-many-requests') {
          throw new Error('Too many failed attempts. Please try again later.');
        }
        throw new Error('Authentication failed. Please try again.');
      }
      // Step 2: Delete all Firestore data FIRST (products + user document)
      // This is done before Auth deletion so the user can retry if it fails
      console.log(`[AccountDeletion] Starting Firestore cleanup for user.id="${user.id}", auth.uid="${currentUser.uid}"`);
      if (user.id !== currentUser.uid) {
        console.warn(`[AccountDeletion] WARNING: user.id (${user.id}) !== currentUser.uid (${currentUser.uid})`);
      }
      try {
        await deleteUserAccountData(user.id);
        console.log('[AccountDeletion] Firestore cleanup succeeded');
      } catch (firestoreError: any) {
        console.error('[AccountDeletion] Firestore cleanup FAILED:', firestoreError);
        throw new Error(`Failed to clean up account data: ${firestoreError.message}. Your account was NOT deleted. Please try again.`);
      }
      // Step 3: Delete Firebase Auth user LAST (point of no return)
      // All Firestore data is already cleaned up, so no orphaned data if this succeeds
      console.log('[AccountDeletion] Deleting Firebase Auth user...');
      await deleteUser(currentUser);
      console.log('[AccountDeletion] Firebase Auth user deleted');
      // Step 4: Log out and redirect
      try {
        await logout();
      } catch (e) {
        /* user already deleted */
      }
      alert('Your account has been deleted successfully.');
      onClose();
      navigate('/');
    } catch (err: any) {
      console.error('Error deleting account:', err);
      setError(err.message || 'An error occurred while deleting your account. Please try again.');
      setIsDeleting(false);
    }
  };
  // Stop propagation to prevent clicks inside the modal from closing it
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4" onClick={onClose} data-id="element-964">
      <div className="bg-white dark:bg-[#162825] rounded-2xl shadow-xl w-full max-w-md relative overflow-hidden" onClick={handleModalClick} data-id="element-965">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-10" data-id="element-966">
          <XIcon className="h-5 w-5" data-id="element-967" />
        </button>
        <div className="p-8" data-id="element-968">
          <div className="flex items-center justify-center mb-4" data-id="element-969">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center text-red-500 dark:text-red-400" data-id="element-970">
              <AlertTriangleIcon className="h-6 w-6" data-id="element-971" />
            </div>
          </div>
          <div className="text-center mb-6" data-id="element-972">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-973">
              Delete Your Account?
            </h2>
            <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-974">
              This action is permanent and cannot be undone. All your data,
              including both buyer and seller profiles, will be permanently
              deleted.
            </p>
          </div>
          {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm" data-id="element-975">
              {error}
            </div>}
          <div className="mb-4" data-id="element-976">
            <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-977">
              Enter your password to verify your identity
            </label>
            <div className="relative" data-id="element-978">
              <input id="confirm-password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-[#1A3330] focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD] pr-10" placeholder="Your password" autoComplete="current-password" data-id="element-979" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-980">
                {showPassword ? <EyeOffIcon className="h-4 w-4" data-id="element-981" /> : <EyeIcon className="h-4 w-4" data-id="element-982" />}
              </button>
            </div>
          </div>
          <div className="mb-6" data-id="element-983">
            <label htmlFor="confirm-delete" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-984">
              Type DELETE to confirm
            </label>
            <input id="confirm-delete" type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)} className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-[#1A3330] focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]" placeholder="DELETE" data-id="element-985" />
          </div>
          <div className="flex space-x-4" data-id="element-986">
            <button onClick={onClose} className="flex-1 py-2 px-4 bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD] rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-[#214541] transition-colors" data-id="element-987">
              Cancel
            </button>
            <button onClick={handleDeleteAccount} disabled={isDeleting || confirmText !== 'DELETE' || !password.trim()} className="flex-1 py-2 px-4 bg-red-500 dark:bg-red-600 text-white rounded-lg font-medium hover:bg-red-600 dark:hover:bg-red-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-988">
              {isDeleting ? <span className="flex items-center justify-center" data-id="element-989">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-990">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-991"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-992"></path>
                  </svg>
                  Deleting...
                </span> : 'Delete Account'}
            </button>
          </div>
        </div>
      </div>
    </div>;
};